// SUB-OFFICE 7 — THE constraint verifier (client-side mirror).
// The authoritative server check lives in src/worker/lib/judge.ts
// (verifyConstraints). This module reproduces that logic EXACTLY so the live
// margin checklist and the server verdict can never disagree:
//   word cap, required word, forbidden word, signature, exclamation law.
// scripts/smoke.mjs proves both implementations identical over fixtures.

import type { Constraint, ConstraintCheck } from './types';

export function wordCount(text: string): number {
  const t = text.trim();
  return t ? t.split(/\s+/).length : 0;
}

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Per-constraint predicate — identical branch logic to the worker's. */
function checkOne(
  text: string,
  c: Constraint,
  designation: string,
  wc: number
): { pass: boolean; detail: string; failure: string | null } {
  switch (c.kind) {
    case 'word_cap': {
      const ok = wc <= (c.max || 60);
      return {
        pass: ok,
        detail: `word cap ${c.max}: ${ok ? 'MET' : 'VIOLATED'} (${wc} words)`,
        failure: ok ? null : `word cap of ${c.max} exceeded (${wc} words)`,
      };
    }
    case 'required_word': {
      const ok = new RegExp(`\\b${escapeRe(c.word || '')}`, 'i').test(text);
      return {
        pass: ok,
        detail: `required word '${c.word}': ${ok ? 'PRESENT' : 'ABSENT'}`,
        failure: ok ? null : `the required word '${c.word}' does not appear`,
      };
    }
    case 'forbidden_word': {
      const present = new RegExp(`\\b${escapeRe(c.word || '')}\\b`, 'i').test(text);
      return {
        pass: !present,
        detail: `forbidden word '${c.word}': ${present ? 'VIOLATED' : 'AVOIDED'}`,
        failure: present ? `the forbidden word '${c.word}' appears` : null,
      };
    }
    case 'signature': {
      const tail = text.slice(-120);
      const numMatch = designation.match(/#\d+-[A-Z]+/);
      const sig = numMatch ? numMatch[0] : designation;
      const ok = tail.toUpperCase().includes(sig.toUpperCase());
      return {
        pass: ok,
        detail: `signature: ${ok ? 'DETECTED' : 'MISSING'}`,
        failure: ok ? null : 'the filing is unsigned',
      };
    }
    case 'exclamation': {
      const present = text.includes('!');
      return {
        pass: !present,
        detail: `exclamation law: ${present ? 'VIOLATED' : 'OBSERVED'}`,
        failure: present ? 'exclamation marks appear in the filing' : null,
      };
    }
  }
}

/**
 * Worker-identical bulk verification: same failures[] strings, same report
 * lines, same order. Used by smoke fixtures and pre-submit gating.
 */
export function verifyConstraints(
  text: string,
  constraints: Constraint[],
  designation: string
): { failures: string[]; report: string } {
  const failures: string[] = [];
  const lines: string[] = [];
  const wc = wordCount(text);
  for (const c of constraints) {
    const r = checkOne(text, c, designation, wc);
    if (r.failure) failures.push(r.failure);
    lines.push(r.detail);
  }
  if (!constraints.length) lines.push('no mechanical constraints attached');
  return { failures, report: lines.join('\n') };
}

/** Live checklist for the compose margin — one row per constraint. */
export function liveChecklist(
  text: string,
  constraints: Constraint[],
  designation: string
): ConstraintCheck[] {
  const wc = wordCount(text);
  return constraints.map((c) => {
    const r = checkOne(text, c, designation, wc);
    return { constraint: c, pass: r.pass, detail: r.detail };
  });
}
